import { useEffect, useMemo, useState } from "react";
import {
    CallAdapter,
    createAzureCommunicationCallAdapterFromClient,
    createStatefulCallClient,
    createStatefulChatClient,
    onResolveVideoEffectDependencyLazy,
    StatefulCallClient,
    StatefulChatClient,
} from "@azure/communication-react";
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from "@azure/communication-common";
import { useDisposeOnPageUnload } from "../utils";
import { CallAgent, GroupCallLocator } from "@azure/communication-calling";

interface PcpCreateStatefulChatClientProps {
    userId: CommunicationUserIdentifier;
    displayName: string;
    endpoint: string;
    token: string;
    locator: GroupCallLocator;
    autoStartCamera?: boolean;
}

export const usePcpCreateStatefulClients = (props: PcpCreateStatefulChatClientProps) => {

    const [callClient, setCallClient] =  useState<StatefulCallClient  |  undefined>(undefined);
    const [callAgent, setCallAgent] =  useState<CallAgent  |  undefined>(undefined);
    const [callAdapter, setCallAdapter] =  useState<CallAdapter  |  undefined>(undefined);

    const [chatClient, setChatClient] =  useState<StatefulChatClient  |  undefined>(undefined);

    useDisposeOnPageUnload(callAdapter, chatClient);

    const token = useMemo(() => props.token, [props.token]);
    const tokenCredential = useMemo(() => new AzureCommunicationTokenCredential(token), [token]);
    const userId = useMemo(
        () => ({ communicationUserId: props.userId.communicationUserId }),
        [props.userId.communicationUserId]
    );
    const roomId = useMemo(
        () => props.locator.groupId,
        [props.locator.groupId]
    );

    useEffect(() => {
        (async () => {
            if (!roomId) {
                return;
            }

            const  statefulCallClient =  createStatefulCallClient({ userId });
            const  callAgent = await statefulCallClient.createCallAgent(tokenCredential, { displayName:  props.displayName });

            // TODO: using ACS groupID as roomID for now
            const  adapter = await createAzureCommunicationCallAdapterFromClient(
                statefulCallClient,
                callAgent,
                { roomId },
                {
                    // see https://azure.github.io/communication-ui-library/?path=/docs/videoeffects--page
                    videoBackgroundOptions: {
                        onResolveDependency: onResolveVideoEffectDependencyLazy,

                        videoBackgroundImages: [
                            {
                                key: 'effect-image-preview-default',
                                url: 'assets/preview-default.png',
                                tooltipText: 'Bild',
                            }
                        ]
                    },
                },
            );

            setCallClient(statefulCallClient);
            setCallAgent(callAgent);
            setCallAdapter(adapter);

            // TODO: student but not moderator
            // initializeIcons();

            await adapter.askDevicePermission({ video:  true, audio:  true });
            await adapter.queryCameras();
            await adapter.queryMicrophones();
            await adapter.querySpeakers();

            if (props.autoStartCamera) {
                await adapter.startCamera();
            }

            const stream = await adapter.createStreamView(undefined, { scalingMode: 'Crop' });
        })();
    }, [props.autoStartCamera, props.displayName, roomId, tokenCredential, userId]);

    useEffect(() => {
        (async () => {
            const statefulChatClient = createStatefulChatClient({
                userId,
                displayName: props.displayName,
                endpoint: props.endpoint,
                credential: tokenCredential,
            });

            await statefulChatClient.startRealtimeNotifications();
            setChatClient(statefulChatClient);
        })();
    }, []);

    return {
        callClient,
        callAgent,
        callAdapter,

        chatClient,
    };
};
